import { NavLink } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { ErrorBoundary } from '@sentry/react'

import { useAuthenticated } from '@jotta/auth-client/useAuthStatus'
import { useBrandingInfo } from '@jotta/grpc-connect-client/config'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { WithAuth } from '@jotta/grpc-connect-client/WithAuth'
import type { Customer } from '@jotta/grpc-connect-openapi/customer'
import { showIntercom } from '@jotta/intercom'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import { LanguageSelector } from '@jotta/ui/LanguageSelector'
import { Notifications } from '@jotta/ui/Notifications'
import { Onboarding } from '@jotta/ui/Onboarding'
import SvgQuestionMark from '@jotta/ui/SvgQuestionMark'
import { useBrandStore, useBrandTheme } from '@jotta/ui/useBrandTheme'
import { Breakpoint, useBreakpoint } from '@jotta/ui/useBreakpoint'
import { UserMenu } from '@jotta/ui/UserMenu'
import { redirectToLogin } from '@jotta/utils/auth'

import { NavigationOverlay } from './NavigationOverlay'

export const Header = observer<{
  onStartSignup?: () => void
}>(function Header({ onStartSignup }) {
  const authenticated = useAuthenticated()
  const branding = useBrandStore()
  const customer = useGetCustomer().data
  const showNavbar = useBreakpoint(Breakpoint.lg)
  const { theme } = useBrandTheme()

  const showLanguageSelector = theme.branding.showLanguageSelector

  if (authenticated === 'UNKNOWN') {
    return null
  }

  if (authenticated === 'TRUE') {
    if (!customer || !customer.username) {
      return null
    }

    return (
      <WithAuth>
        <ErrorBoundary>
          <div
            ref={branding.slots.header}
            id="headercontent"
            data-testid="headerSlot"
          />
        </ErrorBoundary>

        <ErrorBoundary>
          <Notifications />
        </ErrorBoundary>

        <Help customer={customer} />

        {showNavbar && (
          <ErrorBoundary>
            <UserMenu customer={customer} />
          </ErrorBoundary>
        )}

        {!showNavbar && (
          <Onboarding task="navMenu">
            <NavigationOverlay customer={customer} />
          </Onboarding>
        )}
      </WithAuth>
    )
  }

  return (
    <div className="flex h-10 items-center justify-end gap-2 sm:gap-4">
      {onStartSignup && (
        <button className="btn btn-primary" onClick={() => onStartSignup()}>
          <Trans>Sign up</Trans>
        </button>
      )}
      <button className="btn btn-outline" onClick={() => redirectToLogin()}>
        <Trans>Log in</Trans>
      </button>
      {showLanguageSelector && <LanguageSelector />}
    </div>
  )
})

function Help({ customer }: { customer: Customer }) {
  const { supportLink = '' } = useBrandingInfo()

  const hasIntercom = Boolean(customer?.intercom)

  const showHelpButton =
    useBreakpoint(Breakpoint.lg) && (hasIntercom || supportLink)

  if (!showHelpButton) {
    return null
  }

  if (!hasIntercom) {
    return (
      <NavLink
        className="btn btn-elevated"
        aria-label={t`Go to the help pages`}
        to={supportLink}
      >
        <PlainBrandIcon icon={SvgQuestionMark} height={24} aria-hidden="true" />
      </NavLink>
    )
  }

  return (
    <button
      className="btn btn-elevated"
      aria-label={t`Open help`}
      aria-haspopup="dialog"
      onClick={e => showIntercom(e)}
    >
      <PlainBrandIcon icon={SvgQuestionMark} height={24} aria-hidden="true" />
    </button>
  )
}
